import type {
  ImmutableProgram,
  ImmutableResult,
  Cmd,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '@gl/lit/types/architecture.js';
import type {TemplateResult} from 'lit';
import {html} from 'lit';
import type LogsInspectorTable from './index.js';
import {displayApiError} from '@gl/lit/utils/notifications.js';
import type {KindedUnion} from '@gl/lit/types/union.js';
import {createDiscriminator} from '@gl/lit/utils/discriminator.js';
import {runHandlerWithTimeout} from './utils.js';
import {repeat} from 'lit/directives/repeat.js';

export enum ContentState {
  LOADING = 'loading',
  RESOLVED = 'resolved',
}

/**
 * An array of Record<string, unknown> objects where the keys are the column
 * names and the values are the data to be displayed in the table.
 */
export type InspectorLogs = Record<string, unknown>[];

/**
 * A function that returns {@link InspectorLogs}.
 */
export type FetchLogsHandler = () => Promise<InspectorLogs>;

type LogsInspectorContents = KindedUnion<{
  loading: void;
  resolved: {
    logs: InspectorLogs;
  };
}>;

export type Model = {
  contents: LogsInspectorContents;
  fetchLogsHandler?: FetchLogsHandler;
};

const DEFAULT_CONTENTS: LogsInspectorContents = {kind: ContentState.LOADING};

const SKELETON_TABLE_CONFIG = {
  rows: 2,
  cells: 2,
};

export const Msg = makeVariants({
  setContents: Variant<LogsInspectorContents>()('set-contents'),
  setLogs: Variant<InspectorLogs>()('set-logs'),
  setFetchLogsHandler: Variant<Model['fetchLogsHandler']>()(
    'set-fetch-logs-handler'
  ),
});

export type Msg = VariantsFor<typeof Msg>;

// -- Init

function init(): Readonly<Model> {
  return {
    contents: DEFAULT_CONTENTS,
  };
}

// -- Update

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'set-contents':
      return {...model, contents: msg.value} as const;
    case 'set-logs': {
      return {
        ...model,
        contents: {kind: ContentState.RESOLVED, logs: msg.value},
      } as const;
    }
    case 'set-fetch-logs-handler':
      return [
        {...model, fetchLogsHandler: msg.value},
        cmd(async (host: LogsInspectorTable) => {
          const handler = msg.value;
          let logs: InspectorLogs = [];

          try {
            if (handler) {
              host.handleUpdate(Msg.setContents({kind: ContentState.LOADING}));

              logs = await runHandlerWithTimeout<InspectorLogs>(handler);

              host.handleUpdate(
                Msg.setContents({kind: ContentState.RESOLVED, logs})
              );
            }
          } catch (err) {
            displayApiError(err);
            host.handleUpdate(
              Msg.setContents({kind: ContentState.RESOLVED, logs})
            );
          } finally {
            host.handleUpdate(
              Msg.setContents({kind: ContentState.RESOLVED, logs})
            );
          }
        }),
      ];
  }
}

function getColumnsFromLogs(logs: InspectorLogs) {
  const keys = new Set<string>(logs.map(Object.keys).flat());

  return {
    columns: Array.from(keys).map(key => {
      return {label: key};
    }),
    columnFilter: Array.from(keys),
  };
}

function getRowsFromLogs(logs: InspectorLogs) {
  return logs.map(Object.values);
}

function renderLogsTable(logs: InspectorLogs) {
  return html` <fly-table
    hideControls
    .columns=${getColumnsFromLogs(logs).columns}
    .rows=${getRowsFromLogs(logs)}
    .columnFilter=${getColumnsFromLogs(logs).columnFilter}
  ></fly-table>`;
}

function renderLogsInspector(logs: InspectorLogs) {
  return html`<div class="logs-table__container">
    <div class="logs-table__header">
      <slot name="header"></slot>
    </div>
    <div class="logs-table__body">${renderLogsTable(logs)}</div>
    <div class="logs-table__footer">
      <slot name="footer"></slot>
    </div>
  </div>`;
}

function renderSkeletonCell() {
  return html`<td>
    <div class="fly-table__cell">
      <fly-bar-loader width="250px" height="20px"></fly-bar-loader>
    </div>
  </td>`;
}

function renderSkeletonRow() {
  const cells = new Array(SKELETON_TABLE_CONFIG.cells).fill(0);
  return html`<tr class="fly-table__row">
    ${repeat(
      cells,
      (_, idx: number) => `repeatkey:logs-inspector-table-${idx}`,
      () => renderSkeletonCell()
    )}
  </tr>`;
}

function renderSkeletonTable() {
  const rows = new Array(SKELETON_TABLE_CONFIG.rows).fill(0);
  return html`<div class="fly-table-container">
    <div class="fly-table fly-table--rounded fly-table--not-hoverable">
      <table>
        <tbody>
          ${repeat(
            rows,
            (_, idx: number) => `repeatkey:logs-inspector-table-${idx}`,
            () => renderSkeletonRow()
          )}
        </tbody>
      </table>
    </div>
  </div>`;
}

function view(model: Model) {
  const discriminate = createDiscriminator<
    LogsInspectorContents,
    TemplateResult
  >()('kind');

  return html`${discriminate(model.contents)({
    [ContentState.LOADING]: () => renderSkeletonTable(),
    [ContentState.RESOLVED]: ({logs}) => renderLogsInspector(logs),
  })}`;
}

// -- Program

export var main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
